import React, { FC, useCallback, useContext, useEffect, useState } from 'react';

import { Paper } from '@material-ui/core';
import { ValueType } from 'react-select/lib/types';

import { GlobalContext } from '../../../../../store';

import AutoComplete from '../../../../../components/AutoComplete';
import { IOption } from '../../../../../components/Select/Select';
import { getAutoCompleteOptionsArray } from '../../../../../contrib/Utils/getAutoCompleteOptionsArray';
import {
  ICombination,
  scoreTypeOptions,
  useAssignResultsDispatch,
  useAssignResultsState
} from '../AssignResults/Context/AssignResults.context';
import { CombinationCard } from './Components/CombinationCard';
import { getEventFlightsObject } from './Utils/getEventFlightsObject';
import { getFormatsList } from './Utils/getFormatsList';
import { getRoundFormatsList } from './Utils/getRoundFormatsList';
import { useRouteInfoState } from '../../../../../components/RouteInfo/Context/RouteInfo.context';

const LeaderboardFilter: FC = () => {
  const { clubId, eventId, roundId } = useRouteInfoState();

  const {
    dispatch,
    actions: { setCombinations }
  } = useAssignResultsDispatch();

  const {
    combinations,
    eventFormatsList,
    filteredCombinations,
    flights,
    formatsList,
    roundFormatsList,
    selectedFlight,
    selectedFormat,
    selectedScoreType
  } = useAssignResultsState();

  const {
    actions: {
      adminEventFormats: { get: getEventFormats }
    },
    adminEvents,
    adminEventFormats,
    adminRoundFormats
  } = useContext(GlobalContext);

  useEffect(() => {
    if (!roundId) {
      getEventFormats(clubId, eventId);
    }
  }, []);

  useEffect(() => {
    const event = adminEvents[Number(eventId)];
    if (event) {
      dispatch({ type: 'setFlights', value: getEventFlightsObject(event) });
    }
  }, [eventId, adminEvents]);

  const onFormatChange = useCallback(
    (selected: ValueType<IOption>) => {
      if (selected && !Array.isArray(selected)) {
        dispatch({
          type: 'selectFormat',
          value: Number(selected.value)
        });
      } else {
        dispatch({
          type: 'selectFormat',
          value: undefined
        });
      }
    },
    [formatsList]
  );

  const onSelectCombination = (combination: ICombination) => () => {
    dispatch({
      type: 'selectCombination',
      value: combination
    });

    if (!!combination.aggregation) {
      dispatch({
        type: 'selectTeamFormat',
        value: combination.aggregation === 1 ? 'teamStrokeTotal' : 'teamStrokeBetterball'
      });
    }
  };

  const onScoreTypeChange = (selected: ValueType<IOption>) => {
    dispatch({
      type: 'selectScoreType',
      value: selected && !Array.isArray(selected) ? Number(selected.value) : undefined
    });
  };

  const onFlightChange = useCallback(
    (selected: ValueType<IOption>) => {
      dispatch({
        type: 'selectFlight',
        value:
          selected && !Array.isArray(selected) && flights
            ? flights[Number(selected.value)]
            : undefined
      });
    },
    [flights]
  );

  const [flightsOptions, setflightsOptions] = useState<IOption[]>();

  useEffect(() => {
    setflightsOptions(getAutoCompleteOptionsArray(Object.values(flights)));
  }, [flights]);

  const [formatsOptions, setformatsOptions] = useState<IOption[]>();

  useEffect(() => {
    setformatsOptions(getAutoCompleteOptionsArray(Object.values(formatsList)));
  }, [formatsList]);

  useEffect(() => {
    if (roundId && adminRoundFormats && adminRoundFormats[Number(roundId)]) {
      dispatch({
        type: 'setRoundFormatsList',
        value: getRoundFormatsList(adminRoundFormats[Number(roundId)])
      });
    }
  }, [adminRoundFormats, roundId]);

  useEffect(() => {
    if (eventId && adminEventFormats && adminEventFormats[Number(eventId)]) {
      dispatch({
        type: 'setEventFormatsList',
        value: getRoundFormatsList(adminEventFormats[Number(eventId)])
      });
    }
  }, [adminEventFormats, eventId]);

  useEffect(() => {
    if (roundId && adminRoundFormats && adminRoundFormats[Number(roundId)]) {
      dispatch({
        type: 'setFormatsList',
        value: getFormatsList(adminRoundFormats[Number(roundId)])
      });
    }
  }, [adminRoundFormats, roundId]);

  useEffect(() => {
    if (eventId && adminEventFormats && adminEventFormats[Number(eventId)]) {
      dispatch({
        type: 'setFormatsList',
        value: getFormatsList(adminEventFormats[Number(eventId)])
      });
    }
  }, [adminEventFormats, eventId]);

  useEffect(() => {
    if (
      clubId &&
      eventId &&
      roundId &&
      (Object.keys(roundFormatsList).length > 0 || Object.keys(eventFormatsList).length > 0) &&
      Object.keys(flights).length > 0
    ) {
      setCombinations(Number(clubId), Number(eventId), Number(roundId));
    }
  }, [roundFormatsList, eventFormatsList, flights]);

  useEffect(() => {
    dispatch({
      type: 'filterCombinations'
    });
  }, [combinations, selectedScoreType, selectedFlight, selectedFormat]);

  return (
    <Paper elevation={1} className="paper leaderboard-filter">
      <div className="title__container override">
        <h3 className="title-tooltip override">Leaderboard Filter</h3>
      </div>

      <div className="filters">
        <p className="autocomplete-label">SELECT GAME OR FORMAT</p>
        <AutoComplete
          label="label"
          placeholder="SELECT GAME OR FORMAT"
          isSearchable={true}
          options={formatsOptions}
          onChange={onFormatChange}
          isClearable={true}
        />

        <p className="autocomplete-label">SELECT GROSS, NET, ALL</p>
        <AutoComplete
          label="label"
          placeholder="SELECT GROSS, NET, ALL"
          options={scoreTypeOptions}
          onChange={onScoreTypeChange}
          isClearable={true}
        />

        <p className="autocomplete-label">FLIGHTS</p>
        <AutoComplete
          label="label"
          placeholder="SELECT FLIGHT"
          isSearchable={true}
          options={flightsOptions}
          onChange={onFlightChange}
          isClearable={true}
        />
      </div>

      {filteredCombinations && (
        <div className="combinations">
          {filteredCombinations.map((combination: ICombination, index: number) => (
            <CombinationCard
              combination={combination}
              onClick={onSelectCombination(combination)}
              key={`${index}-${combination.flight ? combination.flight.id : -1}-${
                combination.format.id
              }-${combination.format.scoreType}`}
            />
          ))}
        </div>
      )}
    </Paper>
  );
};

export default LeaderboardFilter;
